// Zod shape checks for C2S payloads (43 §7). The room runs these itself and
// answers a failure with `S2C.Error{code:'bad_request'}`; it does not use
// Colyseus `validate()`, which disconnects on any failure. Semantic checks
// (text length after trim, chip ids, walkability) live in the handlers, so
// keep these loose enough never to reject a legitimate client.
import { z } from 'zod';
import { C2S } from './messages';
const int = z.number().int();
const uint8 = int.min(0).max(0xff);
const uint16 = int.min(0).max(0xffff);
const uint32 = int.min(0).max(0xffffffff);
export const TileXYSchema = z.object({ x: uint16, y: uint16 });
export const PingSchema = z.object({ c: z.number().finite() });
export const PresenceSchema = z.object({ set: uint8.default(0), clear: uint8.default(0) });
export const MoveIntentSchema = z.object({
    seq: uint16,
    to: TileXYSchema,
    from: TileXYSchema.optional(),
    path: z.string().max(64).optional(),
    gait: uint8.optional(),
});
export const StopIntentSchema = z.object({ seq: uint16 });
export const InteractRequestSchema = z.object({ obj: uint16, action: uint16, slot: uint8.optional() });
export const InteractCancelSchema = z.object({ obj: uint16 });
// Raw text; the handler trims and enforces SAY_MAX_CHARS.
export const SayMsgSchema = z.object({ text: z.string().max(1024), cmid: uint32 });
// "Emote or chip id" (92 §3.2): exactly one of the two.
export const EmoteMsgSchema = z.union([
    z.object({ emote: int.min(1).max(255), chip: z.undefined().optional() }),
    z.object({ chip: z.string().min(1).max(64), emote: z.undefined().optional() }),
]);
export const TravelRequestSchema = z.object({ to: z.enum(['home', 'area']) });
export const VoiceJoinSchema = z.object({}).optional();
export const VoiceTalkSchema = z.object({ on: z.boolean() });
export const BuildOpsRequestSchema = z.object({
    bseq: uint16,
    baseVersion: uint32,
    ops: z.array(z.record(z.string(), z.unknown())).max(20),
});
export const KnockRequestSchema = z.object({ homeId: z.string().min(1).max(64) });
export const KnockAnswerSchema = z.object({ from: uint32, open: z.boolean() });
export const StallCloseSchema = z.object({}).optional();
export const DevPowerSchema = z.object({ on: z.boolean() });
/**
 * C2S id → payload schema. Hustle messages (110..115) are added by their
 * owner when the akara system lands.
 */
export const C2S_VALIDATORS = {
    [C2S.Ping]: PingSchema,
    [C2S.Presence]: PresenceSchema,
    [C2S.Move]: MoveIntentSchema,
    [C2S.Stop]: StopIntentSchema,
    [C2S.Interact]: InteractRequestSchema,
    [C2S.InteractCancel]: InteractCancelSchema,
    [C2S.Say]: SayMsgSchema,
    [C2S.Emote]: EmoteMsgSchema,
    [C2S.Travel]: TravelRequestSchema,
    [C2S.VoiceJoin]: VoiceJoinSchema,
    [C2S.VoiceTalk]: VoiceTalkSchema,
    [C2S.BuildOps]: BuildOpsRequestSchema,
    [C2S.Knock]: KnockRequestSchema,
    [C2S.KnockAnswer]: KnockAnswerSchema,
    [C2S.StallClose]: StallCloseSchema,
    [C2S.DevPower]: DevPowerSchema,
};
